import { useEffect, useState } from 'react';
import { ExclamationCircleOutlined, EllipsisOutlined, ReloadOutlined, PlusOutlined, ApiOutlined } from '@ant-design/icons';
import { Typography, Modal, Menu, Dropdown, Button, PageHeader, Table, Layout } from 'antd';
import { NavLink } from 'react-router-dom';
import { apiClient, apiErrorMessage } from '../../../utils';
import { NewIntegrationPage } from './new-integration-page';
import { Integration } from '../../../models';
import moment from 'moment';
import { TableNameColumn } from '../../../elements';
import { EntityChangeLog } from '../../reports';

export function IntegrationsPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [integrations, setIntegrations] = useState<(Integration & { key: string })[]>();

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = () => {
    setLoading(true);
    apiClient
      .get('integrations')
      .then(response => {
        const responseIntegrations: Integration[] = response.data;
        if (responseIntegrations?.length) {
          const responseData = responseIntegrations.map(integration => {
            return {
              key: integration.id,
              ...integration,
            };
          });
          setIntegrations(responseData);
        }
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const openAddModal = () => {
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
  };

  const openChangeLogModal = (id: string) => {
    setChangeLogModalOpen(id);
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const onRefreshClick = () => {
    loadIntegrations();
  };

  const onIntegrationAdded = () => {
    closeAddModal();
    loadIntegrations();
  };

  const deleteIntegration = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`integrations/${id}`)
      .then(() => {
        setLoading(false);
        loadIntegrations();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmDeleteIntegration = (id: string) => {
    const onOk = () => deleteIntegration(id);
    Modal.confirm({
      title: 'Entegrasyon sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Entegrasyonu silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'changelog') {
      openChangeLogModal(id);
    } else if (e.key === 'del') {
      confirmDeleteIntegration(id);
    }
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      <Menu.Item key="edit">
        <NavLink to={'/admin/integrations/' + id}>Düzenle</NavLink>
      </Menu.Item>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      <Menu.Item key="del">
        <Text type="danger">Sil</Text>
      </Menu.Item>
    </Menu>
  );

  const tableColumns = [
    {
      title: 'İsim',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, integration: Integration) => (
        <TableNameColumn name={name} description={integration.description} path={'/admin/integrations/' + integration.id} />
      ),
    },
    {
      title: 'Entegrasyon türü',
      dataIndex: 'integrationType',
      key: 'integrationType',
      render: (integrationType: string, integration: Integration) =>
        integrationType === 'IMPORT' ? 'İçe entegrasyon' : 'Dışa entegrasyon',
    },
    {
      title: 'Son aktarım',
      dataIndex: 'lastExecutionTime',
      key: 'lastTime',
      render: (_: any, integration: Integration) =>
        integration.lastExecution ? moment(integration.lastExecution.createdAt).format('DD.MM.yyyy HH:mm:ss') : '-',
    },
    {
      title: 'Kayıt sayısı',
      dataIndex: 'recordCount',
      key: 'lastCount',
      render: (_: any, integration: Integration) => (integration.lastExecution ? integration.lastExecution.recordCount : '-'),
    },
    {
      title: '',
      key: 'action',
      render: (_: any, integrtion: Integration) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(integrtion.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <ApiOutlined /> Entegrasyonlar
          </>
        }
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <Button key="add" onClick={openAddModal} icon={<PlusOutlined />} type="primary">
            Yeni entegrasyon
          </Button>,
        ]}
      />
      <Content>
        <Table columns={tableColumns} dataSource={integrations} loading={loading} scroll={{ x: '100%' }} pagination={false} />
      </Content>
      <Modal title="Yeni entegrasyon" visible={addModalOpen} footer={null} onCancel={closeAddModal} destroyOnClose={true}>
        <NewIntegrationPage onSaved={onIntegrationAdded} />
      </Modal>
      <Modal
        title="Entegrasyon değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'Integration'} recordId={changeLogModalOpen} />
      </Modal>
    </>
  );
}
